import { computed, observable } from 'mobx'
import { AddonType, PlanType, SalonMemberPermission, SoloSubscriptionPlatform } from '../../../graphQL/generated/enums'
import { $RootStore } from '../../../src/stores/RootStore'
import { priorityOrder, plansToHide, EntitlementId, entitlementGracePeriods } from './consts.subscription'
import moment from 'moment'
import type { SalonMember } from '../SalonStore/SalonStore'

export { plansToHide, priorityOrder, EntitlementId }

export default class BaseSubscriptionStore {
  rootStore: $RootStore
  @observable accessor plans: SoloPlanWithPrice[] = []
  @observable accessor addonPrices: SoloAddonPrice[] = []
  @observable accessor nextBillingEstimates: NextBillingEstimate[] = []

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore
  }

  clearData = () => {
    this.plans = []
    this.addonPrices = []
  }

  fetchPlans = async () => {
    const { communication, userStore } = this.rootStore
    const response = await communication.requester.plansWithPrice({ currencyCode: userStore.data.currencyCode || 'GBP', period: 'monthly' })
    this.plans = response.plansWithPrice || []
  }

  fetchAddonPrices = async (currencyCode = this.rootStore.userStore.data.currencyCode || 'GBP') => {
    const { communication } = this.rootStore

    try {
      const response = await communication.requester.addonPrices({ currencyCode, period: 'monthly' })
      this.addonPrices = response.addonPrices || []
    } catch {
      this.addonPrices = []
    }
  }

  showLinkError = () => {
    const { dropdownMessageHandler, i18n: { s } } = this.rootStore
    dropdownMessageHandler.showDropdownMessage({
      type: 'error',
      title: s('Failed'),
      message: s('There was an issue creating the subscription link. Please try again'),
    })
  }

  runTestSubscriptionAction = async (data: SoloSubscriptionTestData) => {
    const { dropdownMessageHandler, i18n: { s } } = this.rootStore

    try {
      await this.rootStore.communication.requester.testSubscription({ data })
      return true
    } catch (error) {
      dropdownMessageHandler.showDropdownMessage({
        type: 'error',
        title: s('Failed'),
        message: s('Error running the test subscription. Please try again'),
        closeInterval: 500,
      })
    }

    return false
  }

  syncWithChargebee = async () => {
    const { communication } = this.rootStore

    if (!this.rootStore.userStore.data.subscriptionDetails?.chargeBeeId) return

    await communication.requester.syncWithChargebee()
  }

  daysSinceEntitlementActive(entitlementId: typeof EntitlementId[keyof typeof EntitlementId]): number {
    const entitlementOverrides = this.rootStore.userStore.data.subscriptionDetails?.entitlementOverrides ?? []
    if (entitlementOverrides.includes(entitlementId)) return 0

    const entitlement = this.entitlements.find(entitlement => entitlement.id === entitlementId)
    if (entitlement?.enabled === true) return 0

    if (!entitlement?.lastActiveAt) return Infinity

    const hoursSinceActive = moment().diff(moment(entitlement?.lastActiveAt), 'hours')
    const daysSinceActive = Math.floor(hoursSinceActive / 24)

    return daysSinceActive
  }

  inGracePeriod(entitlementId: typeof EntitlementId[keyof typeof EntitlementId]) {
    const entitlement = this.entitlements.find(entitlement => entitlement.id === entitlementId)
    return entitlement?.enabled === false && this.daysSinceEntitlementActive(entitlementId) <= entitlementGracePeriods[entitlementId]
  }

  hasEntitlement(entitlementId: typeof EntitlementId[keyof typeof EntitlementId]) {
    const entitlement = this.entitlements.find(entitlement => entitlement.id === entitlementId)
    const entitlementOverrides = this.rootStore.userStore.data.subscriptionDetails?.entitlementOverrides ?? []

    return entitlementOverrides.includes(entitlementId)
      || entitlement?.enabled === true
      || this.inGracePeriod(entitlementId)
  }

  @computed get getPlatform() {
    return SoloSubscriptionPlatform.Web
  }

  @computed get activeSubscriptions() {
    return this.subscriptionList.filter(subscription => subscription.status === 'active')
  }

  @computed get showBookingSystemUpgradePlanMessage() {
    const daysSinceActive = this.daysSinceEntitlementActive(EntitlementId.BOOKING_SYSTEM)
    return daysSinceActive > 0
  }

  @computed get hasBookingSystemEntitlement() {
    return this.hasEntitlement(EntitlementId.BOOKING_SYSTEM)
  }

  @computed get hasWebsiteEntitlement() {
    return this.hasEntitlement(EntitlementId.WEBSITE_BUILDER)
  }

  @computed get daysSinceBookingSystemEntitlementActive() {
    return this.daysSinceEntitlementActive(EntitlementId.BOOKING_SYSTEM)
  }

  @computed get remainingBookingSystemEntitlementGracePeriod() {
    return entitlementGracePeriods[EntitlementId.BOOKING_SYSTEM] - this.daysSinceBookingSystemEntitlementActive
  }

  @computed get daysSinceWebsiteEntitlementActive() {
    return this.daysSinceEntitlementActive(EntitlementId.WEBSITE_BUILDER)
  }

  @computed get entitlements() {
    return this.rootStore.userStore.data.subscriptionDetails?.entitlements ?? []
  }

  @computed get salonMemberEntitlementCount(): number {
    return parseInt(this.entitlements.find(entitlement => entitlement.id === EntitlementId.SALON_MEMBER)?.value ?? '0')
  }

  // Owners and receptionists are not counted towards the limit
  memberUsesSeat = ({ permissions }: SalonMember): boolean => {
    const isOwner = permissions.includes(SalonMemberPermission.Owner)
    const isReceptionist = permissions.length === 1 && permissions[0] === SalonMemberPermission.Receptionist
    return !isOwner && !isReceptionist
  }

  @computed get salonMemberSeatsUsed(): number {
    return this.rootStore.salonStore.members.filter(this.memberUsesSeat).length
  }

  @computed get salonMemberSpareSeats(): number {
    return Math.max(this.salonMemberEntitlementCount - this.salonMemberSeatsUsed, 0)
  }

  @computed get isOverSalonMemberSeatLimit(): boolean {
    if (!this.rootStore.authStore.isSalonOwnerOrAdminSession) return false

    return this.salonMemberSeatsUsed > this.salonMemberEntitlementCount
  }

  @computed get salonMemberSeatPricePennies(): number | undefined {
    return this.addonPrices.find(addon => addon.type === AddonType.SalonMemberSeat)?.price_pennies
  }

  @computed get subscriptionList() {
    const { subscriptionDetails } = this.rootStore.userStore.data
    const { saasSubscriptions } = subscriptionDetails ?? {}

    if (!saasSubscriptions || saasSubscriptions.length === 0) {
      return []
    }

    // Sort by status, then trial start date
    return saasSubscriptions.slice()
      .sort((a, b) => a.status === 'active' ? -1 : b.status === 'active' ? 1 : (a.trialStart ?? 0) - (b.trialStart ?? 0))
  }

  @computed get bookingSystemSubscription() {
    return this.subscriptionList
      .find(subscription => subscription.planType === PlanType.Pro)
  }

  @computed get isInBookingSystemTrial(): boolean {
    const subscription = this.bookingSystemSubscription
    return subscription?.status === 'active' && !!subscription.inTrial
  }

  @computed get websiteSubscription() {
    return this.subscriptionList
      .find(subscription => subscription.planType === PlanType.Website)
  }

  @computed get bookingsThisPeriod() {
    const { subscriptionDetails } = this.rootStore.userStore.data
    return subscriptionDetails?.saasBookingsThisPeriod?.value ?? 1e9
  }

  @computed get canExtendTrial() {
    const hasAddedManyBookings = this.bookingsThisPeriod > 10
    if (hasAddedManyBookings) return false

    const { subscriptionDetails } = this.rootStore.userStore.data
    const hasExtendedTrial = (subscriptionDetails?.trialExtensions?.length ?? 0) > 0
    if (hasExtendedTrial) return false

    return true
  }

  @computed get loading() {
    const subscriptionLoadingOperations = [
      'testSubscription',
      'manageSoloSubscriptionUrl',
      'soloSubscriptionUrl',
      'plansWithPrice',
    ]

    return this.rootStore.uiStore.activeRequests.some(request => subscriptionLoadingOperations.includes(request.operationName))
  }
}